import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import './Login.css';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/brand/logo.png`;

/**
 * UZ | EN | RU. The sign-in pages open in Uzbek, and the top bar (where the switch normally
 * lives) is not on them, so the language has to be changeable here.
 */
export function AuthLang() {
  const { t, lang, setLang, languages } = useI18n();
  return (
    <div className="auth-lang" role="group" aria-label={t('auth.language')}>
      {languages.map((item) => (
        <button
          key={item.code}
          type="button"
          lang={item.code}
          title={item.label}
          className={`auth-lang-btn${item.code === lang ? ' is-active' : ''}`}
          aria-pressed={item.code === lang}
          onClick={() => setLang(item.code)}
        >
          {item.short}
        </button>
      ))}
    </div>
  );
}

/** The step list on the brand panel. `icon` is a lucide element. */
function Features({ items }) {
  if (!items.length) return null;
  return (
    <ul className="auth-features">
      {items.map((item) => (
        <li className="auth-feature" key={item.title}>
          <span className="auth-feature-icon" aria-hidden="true">{item.icon}</span>
          <div className="auth-feature-text">
            <strong>{item.title}</strong>
            <span>{item.desc}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * The frame of every public auth page: a yellow brand panel beside one white card
 * (stacked on phones and tablets), a language switch, and the document title.
 *
 * @param {string} title - the card's heading; also the browser tab title
 * @param {string} [badge] - the pill next to the logo
 * @param {{to: string, label: string}} [back] - back link above the card
 * @param {React.ReactNode | React.ReactNode[]} [below] - a line under the card (the "no account?"
 *   prompt); an array gives one line per item
 * @param {boolean} [shake] - shake the card once (a refused sign-in)
 */
function AuthLayout({
  title,
  badge = 'CRM',
  tagline,
  taglineEm,
  desc,
  features = [],
  back,
  below,
  shake = false,
  children,
}) {
  // The tab title follows the page (and its steps); leaving the page restores the old one.
  useEffect(() => {
    const original = document.title;
    return () => { document.title = original; };
  }, []);
  useEffect(() => {
    if (title) document.title = `${title} · Darsly CRM`;
  }, [title]);

  return (
    <div className="auth-page">
      <AuthLang />

      <aside className="auth-left" data-surface="brand">
        <div className="auth-left-pattern" aria-hidden="true" />
        <div className="auth-brand">
          <div className="auth-logo">
            <img src={LOGO_SRC} alt="Darsly" className="auth-logo-img" />
            <span className={`auth-logo-sub${badge.length > 4 ? ' auth-logo-sub--long' : ''}`}>{badge}</span>
          </div>

          <p className="auth-tagline">
            {tagline}<br />
            <em>{taglineEm}</em>
          </p>
          {desc && <p className="auth-desc">{desc}</p>}
          <Features items={features} />
        </div>
      </aside>

      <main className="auth-right">
        <div className="auth-form-wrap">
          {back && (
            <Link to={back.to} className="auth-back-link">
              <ArrowLeft size={16} aria-hidden="true" /> {back.label}
            </Link>
          )}
          <div className={`auth-card${shake ? ' auth-card--shake' : ''}`}>{children}</div>
          {/* the lines never reorder, so their position is a stable key */}
          {(Array.isArray(below) ? below : [below]).filter(Boolean).map((line, index) => (
            <p className="auth-switch" key={`line-${index}`}>{line}</p>
          ))}
        </div>
      </main>
    </div>
  );
}

export default AuthLayout;
